import { useEffect, useRef, useState } from "react";
/* eslint-disable react/no-unescaped-entities */
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    Circle,
    CircleDashed,
    File,
    FileText,
    Trash2,
    Upload,
    ChevronDown,
    ChevronUp,
    Eye,
    EyeOff,
    Inbox,
    Lock,
    Paperclip,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";
import { Field, Select } from "./CockpitDossiers";
import { AIIntelligence } from "./CockpitAIPanel";
import { InvitesPanel, ReportsPanel } from "./CockpitClientPanels";
import { FinancialPanel, OpportunitiesPanel } from "./CockpitFinancialPanels";
import { DossierContactsPanel, ClientAccessPanel } from "./CockpitDossierContacts";
import { StrategicReadingPanel, StrategicSummaryHeader } from "./CockpitStrategicReading";
import { ClientShareablesPanel } from "./CockpitClientShareables";
import { NetworkLinksPanel } from "./CockpitNetworkLinks";
import ValuationPanel from "./CockpitValuationPanel";
import DossierAdminImpact from "./CockpitDossierAdminImpact";
import CockpitDossierModulesPanel from "./CockpitDossierModulesPanel";
import CockpitCommitteePanel from "./CockpitCommitteePanel";

const STATUS = [
    { v: "draft", l: "Brouillon" },
    { v: "audit", l: "Audit initial" },
    { v: "analyse", l: "Analyse stratégique" },
    { v: "structuration", l: "Structuration" },
    { v: "qualification", l: "Qualification" },
    { v: "discussion", l: "Discussions" },
    { v: "closing", l: "Closing" },
    { v: "live", l: "En cours" },
    { v: "paused", l: "Pause" },
    { v: "closed", l: "Clos" },
];

const CONFIDENTIALITY = [
    { v: "standard", l: "Standard" },
    { v: "restricted", l: "Restreint" },
    { v: "ultra", l: "Ultra-confidentiel" },
];

const PIPELINE_STAGES_LIST = [
    { v: "", l: "—" },
    { v: "prospect", l: "Prospect" },
    { v: "qualification", l: "Qualification" },
    { v: "audit", l: "Audit" },
    { v: "structuration", l: "Structuration" },
    { v: "discussions", l: "Discussions" },
    { v: "finalisation", l: "Finalisation" },
    { v: "clos", l: "Clos" },
];

const SOURCE_LIST = [
    { v: "", l: "—" },
    ...[
        "Réseau", "Recommandation", "LinkedIn", "Instagram",
        "Family Office", "Collectivité", "Partenaire", "Événement", "Autre",
    ].map((v) => ({ v, l: v })),
];

const MATURITY_LEVEL_LIST = [
    { v: "", l: "—" },
    { v: "faible", l: "Faible" },
    { v: "sérieux", l: "Sérieux" },
    { v: "avancé", l: "Avancé" },
    { v: "prioritaire", l: "Prioritaire" },
];

// ── Grille Tarifaire XV — option lists ──────────────────────
const AMOUNT_BAND_LIST = [
    { v: "", l: "—" },
    { v: "<500k", l: "< 500 k€" },
    { v: "500k-1M", l: "500 k€ — 1 M€" },
    { v: "1M-5M", l: "1 M€ — 5 M€" },
    { v: "5M-20M", l: "5 M€ — 20 M€" },
    { v: ">20M", l: "> 20 M€" },
];
const COMPLEXITY_LIST = [
    { v: "", l: "—" },
    { v: "simple", l: "Simple" },
    { v: "intermédiaire", l: "Intermédiaire" },
    { v: "complexe", l: "Complexe" },
    { v: "très complexe", l: "Très complexe" },
];
const STAKEHOLDERS_LIST = [
    { v: "", l: "—" },
    { v: "1-2", l: "1 à 2" },
    { v: "3-5", l: "3 à 5" },
    { v: "6+", l: "6 et plus" },
];
const URGENCY_LIST = [
    { v: "", l: "—" },
    { v: "normal", l: "Normal" },
    { v: "urgent", l: "Urgent" },
    { v: "critique", l: "Critique" },
];
const NETWORK_NEED_LIST = [
    { v: "", l: "—" },
    { v: "faible", l: "Faible" },
    { v: "moyen", l: "Moyen" },
    { v: "fort", l: "Fort" },
    { v: "stratégique", l: "Stratégique" },
];
const SUCCESS_POT_LIST = [
    { v: "", l: "—" },
    { v: "faible", l: "Faible" },
    { v: "moyen", l: "Moyen" },
    { v: "fort", l: "Fort" },
];

const NEXT_STATE = { pending: "in_progress", in_progress: "done", done: "pending" };

function formatBytes(n) {
    if (!n) return "0 o";
    const k = 1024;
    const sizes = ["o", "Ko", "Mo", "Go"];
    const i = Math.floor(Math.log(n) / Math.log(k));
    return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function CockpitDossierDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [dossier, setDossier] = useState(null);
    const [notes, setNotes] = useState([]);
    const [docs, setDocs] = useState([]);
    const [partners, setPartners] = useState([]);
    const [newNote, setNewNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState(null);
    const [openStepId, setOpenStepId] = useState(null);
    const fileInputRef = useRef(null);

    const load = async () => {
        const [d, n, dd, pp] = await Promise.all([
            cockpit.get(`/dossiers/${id}`).then((r) => r.data),
            cockpit.get(`/dossiers/${id}/notes`).then((r) => r.data),
            cockpit.get(`/dossiers/${id}/documents`).then((r) => r.data),
            cockpit.get(`/partners`).then((r) => r.data),
        ]);
        setDossier(d);
        setEdit(d);
        setNotes(n);
        setDocs(dd);
        setPartners(pp);
    };

    useEffect(() => {
        load().catch(() => nav("/cockpit/dossiers"));
    }, [id]);

    if (!dossier)
        return (
            <div className="p-16 text-[#f4ecd8]/40 text-sm">Chargement du dossier…</div>
        );

    const toggleStep = async (stepId) => {
        const next = dossier.timeline.map((s) =>
            s.id === stepId ? { ...s, state: NEXT_STATE[s.state] } : s
        );
        const { data } = await cockpit.put(`/dossiers/${id}/timeline`, { steps: next });
        setDossier(data);
    };

    const saveStepReport = async (stepId, { title, body, validated }) => {
        const { data } = await cockpit.put(
            `/dossiers/${id}/timeline/${stepId}/report`,
            { title, body, validated }
        );
        setDossier(data);
    };

    const addStepReport = async (stepId, { title, body, validated }) => {
        const { data } = await cockpit.post(
            `/dossiers/${id}/timeline/${stepId}/reports`,
            { title, body, validated },
        );
        setDossier(data);
    };

    const updateStepReportById = async (stepId, reportId, { title, body, validated }) => {
        const { data } = await cockpit.put(
            `/dossiers/${id}/timeline/${stepId}/reports/${reportId}`,
            { title, body, validated },
        );
        setDossier(data);
    };

    const deleteStepReportById = async (stepId, reportId) => {
        if (!window.confirm("Supprimer cette synthèse ?")) return;
        await cockpit.delete(
            `/dossiers/${id}/timeline/${stepId}/reports/${reportId}`,
        );
        const { data } = await cockpit.get(`/dossiers/${id}`);
        setDossier(data);
    };

    const deleteStepReport = async (stepId) => {
        if (!window.confirm("Supprimer toutes les synthèses de cette étape ?")) return;
        await cockpit.delete(`/dossiers/${id}/timeline/${stepId}/report`);
        const { data } = await cockpit.get(`/dossiers/${id}`);
        setDossier(data);
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        setBusy(true);
        try {
            const { data } = await cockpit.post(`/dossiers/${id}/notes`, {
                body: newNote.trim(),
            });
            setNotes([data, ...notes]);
            setNewNote("");
        } finally {
            setBusy(false);
        }
    };

    const deleteNote = async (noteId) => {
        await cockpit.delete(`/dossiers/${id}/notes/${noteId}`);
        setNotes(notes.filter((n) => n.id !== noteId));
    };

    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            await cockpit.post(`/dossiers/${id}/documents`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const { data } = await cockpit.get(`/dossiers/${id}/documents`);
            setDocs(data);
        } finally {
            setBusy(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const downloadDoc = async (doc) => {
        const res = await cockpit.get(
            `/dossiers/${id}/documents/${doc.id}`,
            { responseType: "blob" }
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const deleteDoc = async (docId) => {
        await cockpit.delete(`/dossiers/${id}/documents/${docId}`);
        setDocs(docs.filter((d) => d.id !== docId));
    };

    const toggleShare = async (doc) => {
        const next = !doc.shared_with_client;
        await cockpit.put(`/documents/${doc.id}/share`, {
            shared_with_client: next,
        });
        setDocs(
            docs.map((d) =>
                d.id === doc.id ? { ...d, shared_with_client: next } : d
            )
        );
    };

    const saveMeta = async () => {
        const payload = {
            name: edit.name,
            sector: edit.sector,
            geography: edit.geography,
            operation_type: edit.operation_type,
            maturity: edit.maturity,
            ticket: edit.ticket,
            confidentiality: edit.confidentiality,
            status: edit.status,
            summary: edit.summary,
            pipeline_stage: edit.pipeline_stage || "",
            source: edit.source || "",
            maturity_level: edit.maturity_level || "",
            revenue_target: Number(edit.revenue_target) || 0,
            vat_rate: edit.vat_rate === "" || edit.vat_rate === undefined || edit.vat_rate === null
                ? null
                : Number(edit.vat_rate),
            vat_exempt: !!edit.vat_exempt,
            estimated_hours: Number(edit.estimated_hours) || 0,
            hours_spent: Number(edit.hours_spent) || 0,
            future_opportunities: edit.future_opportunities || "",
            development_potential: edit.development_potential || [],
            // ── Grille Tarifaire XV — valuation criteria ────
            mission_type: edit.mission_type || "",
            operation_amount_band: edit.operation_amount_band || "",
            complexity: edit.complexity || "",
            stakeholders_band: edit.stakeholders_band || "",
            urgency: edit.urgency || "",
            network_need: edit.network_need || "",
            international_dim: !!edit.international_dim,
            success_fee_potential: edit.success_fee_potential || "",
        };
        const { data } = await cockpit.put(`/dossiers/${id}`, payload);
        setDossier(data);
        setEditing(false);
    };

    const deleteDossier = async () => {
        if (!window.confirm("Supprimer définitivement ce dossier ?")) return;
        await cockpit.delete(`/dossiers/${id}`);
        nav("/cockpit/dossiers");
    };

    return (
        <div data-testid="cockpit-dossier-detail" className="px-5 sm:px-8 lg:px-16 py-8 lg:py-12">
            <Link
                to="/cockpit/dossiers"
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/70 hover:text-[#d4af37] transition-colors duration-500 mb-10"
            >
                <ArrowLeft strokeWidth={1.2} size={12} />
                Retour aux dossiers
            </Link>

            <header className="mb-12 grid lg:grid-cols-12 gap-10 items-end">
                <div className="lg:col-span-8">
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Dossier confidentiel
                    </div>
                    <h1
                        className="font-serif text-[26px] sm:text-[34px] md:text-[44px] lg:text-[52px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        {dossier.name}
                    </h1>
                    <div className="mt-5 flex flex-wrap gap-4 text-[12px] text-[#f4ecd8]/55 tracking-wide">
                        <Tag label="Statut" value={STATUS.find((s) => s.v === dossier.status)?.l || dossier.status} accent />
                        {dossier.pipeline_stage && (
                            <Tag
                                label="Pipeline"
                                value={PIPELINE_STAGES_LIST.find((s) => s.v === dossier.pipeline_stage)?.l}
                                accent
                            />
                        )}
                        {dossier.maturity_level && (
                            <Tag
                                label="Maturité"
                                value={MATURITY_LEVEL_LIST.find((m) => m.v === dossier.maturity_level)?.l}
                            />
                        )}
                        {dossier.source && <Tag label="Origine" value={dossier.source} />}
                        {dossier.sector && <Tag label="Secteur" value={dossier.sector} />}
                        {dossier.geography && <Tag label="Géographie" value={dossier.geography} />}
                        {dossier.ticket && <Tag label="Ticket" value={dossier.ticket} />}
                        {dossier.operation_type && <Tag label="Opération" value={dossier.operation_type} />}
                        <Tag label="Confidentialité" value={CONFIDENTIALITY.find((c) => c.v === dossier.confidentiality)?.l} />
                    </div>
                    <StrategicSummaryHeader reading={dossier.strategic_reading} />
                </div>
                <div className="lg:col-span-4 flex flex-wrap lg:justify-end gap-3">
                    {(["draft", "audit", "analyse"].includes(dossier.status)) && (
                        <button
                            type="button"
                            data-testid="dossier-convert-client"
                            onClick={async () => {
                                if (!window.confirm(`Convertir "${dossier.name}" en client ? (statut → Structuration)`)) return;
                                try {
                                    const { data } = await cockpit.post(
                                        `/dossiers/${id}/convert-to-client`,
                                    );
                                    setDossier(data);
                                    setEdit(data);
                                } catch (e) {
                                    alert(e?.response?.data?.detail || "Erreur");
                                }
                            }}
                            className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                        >
                            Convertir en client
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="dossier-edit-toggle"
                        onClick={() => setEditing((e) => !e)}
                        className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-white/15 text-[#f4ecd8]/70 hover:text-[#f4ecd8] hover:border-white/30 transition-all duration-500"
                    >
                        {editing ? "Annuler" : "Modifier"}
                    </button>
                    <button
                        type="button"
                        onClick={deleteDossier}
                        className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#e89090]/40 text-[#e89090]/80 hover:text-[#e89090] hover:border-[#e89090] transition-all duration-500"
                    >
                        Supprimer
                    </button>
                </div>
            </header>

            {editing && (
                <section
                    data-testid="dossier-edit-form"
                    className="bg-[#070d18] border border-white/[0.08] p-8 lg:p-10 mb-14"
                >
                    <div className="overline text-[#d4af37]/80 mb-6">
                        Édition du dossier
                    </div>
                    <div className="space-y-6">
                        <Field label="Nom" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                        <div className="grid sm:grid-cols-2 gap-6">
                            <Field label="Secteur" value={edit.sector} onChange={(e) => setEdit({ ...edit, sector: e.target.value })} />
                            <Field label="Géographie" value={edit.geography} onChange={(e) => setEdit({ ...edit, geography: e.target.value })} />
                            <Field label="Type d'opération" value={edit.operation_type} onChange={(e) => setEdit({ ...edit, operation_type: e.target.value })} />
                            <Field label="Ticket" value={edit.ticket} onChange={(e) => setEdit({ ...edit, ticket: e.target.value })} />
                            <Field label="Maturité" value={edit.maturity} onChange={(e) => setEdit({ ...edit, maturity: e.target.value })} />
                            <Select label="Statut" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} options={STATUS} />
                            <Select label="Confidentialité" value={edit.confidentiality} onChange={(e) => setEdit({ ...edit, confidentiality: e.target.value })} options={CONFIDENTIALITY} />
                            <Field
                                label="Chiffre d'affaires cible (€)"
                                type="number"
                                value={edit.revenue_target ?? 0}
                                onChange={(e) => setEdit({ ...edit, revenue_target: e.target.value })}
                                testid="dossier-revenue-target"
                            />
                        </div>
                        <div className="pt-4 border-t border-white/[0.06]">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-5">
                                Paramètres financiers &amp; capacité
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                <Field
                                    label="TVA dossier (%) — vide = défaut"
                                    type="number"
                                    value={edit.vat_rate ?? ""}
                                    onChange={(e) => setEdit({ ...edit, vat_rate: e.target.value })}
                                    testid="dossier-vat-rate"
                                />
                                <div className="flex flex-col">
                                    <label className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                                        Exonéré TVA
                                    </label>
                                    <label className="inline-flex items-center gap-3 cursor-pointer mt-2">
                                        <input
                                            type="checkbox"
                                            data-testid="dossier-vat-exempt"
                                            checked={!!edit.vat_exempt}
                                            onChange={(e) => setEdit({ ...edit, vat_exempt: e.target.checked })}
                                            className="accent-[#d4af37]"
                                        />
                                        <span className="text-[13px] font-serif text-[#f4ecd8]/85">
                                            Hors champ TVA
                                        </span>
                                    </label>
                                </div>
                                <Field
                                    label="Heures estimées"
                                    type="number"
                                    value={edit.estimated_hours ?? 0}
                                    onChange={(e) => setEdit({ ...edit, estimated_hours: e.target.value })}
                                    testid="dossier-estimated-hours"
                                />
                                <Field
                                    label="Heures passées"
                                    type="number"
                                    value={edit.hours_spent ?? 0}
                                    onChange={(e) => setEdit({ ...edit, hours_spent: e.target.value })}
                                    testid="dossier-hours-spent"
                                />
                            </div>
                        </div>
                        <div className="pt-4 border-t border-white/[0.06]">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-5">
                                Pilotage commercial
                            </div>
                            <div className="grid sm:grid-cols-3 gap-6">
                                <Select
                                    label="Phase pipeline"
                                    value={edit.pipeline_stage || ""}
                                    onChange={(e) => setEdit({ ...edit, pipeline_stage: e.target.value })}
                                    options={PIPELINE_STAGES_LIST}
                                    testid="dossier-pipeline-stage"
                                />
                                <Select
                                    label="Origine"
                                    value={edit.source || ""}
                                    onChange={(e) => setEdit({ ...edit, source: e.target.value })}
                                    options={SOURCE_LIST}
                                    testid="dossier-source"
                                />
                                <Select
                                    label="Maturité"
                                    value={edit.maturity_level || ""}
                                    onChange={(e) => setEdit({ ...edit, maturity_level: e.target.value })}
                                    options={MATURITY_LEVEL_LIST}
                                    testid="dossier-maturity-level"
                                />
                            </div>
                        </div>
                        <div className="pt-4 border-t border-white/[0.06]">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-5">
                                Critères de valorisation (grille XV)
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                <Select
                                    label="Montant de l'opération"
                                    value={edit.operation_amount_band || ""}
                                    onChange={(e) => setEdit({ ...edit, operation_amount_band: e.target.value })}
                                    options={AMOUNT_BAND_LIST}
                                    testid="dossier-amount-band"
                                />
                                <Select
                                    label="Complexité"
                                    value={edit.complexity || ""}
                                    onChange={(e) => setEdit({ ...edit, complexity: e.target.value })}
                                    options={COMPLEXITY_LIST}
                                    testid="dossier-complexity"
                                />
                                <Select
                                    label="Parties prenantes"
                                    value={edit.stakeholders_band || ""}
                                    onChange={(e) => setEdit({ ...edit, stakeholders_band: e.target.value })}
                                    options={STAKEHOLDERS_LIST}
                                    testid="dossier-stakeholders"
                                />
                                <Select
                                    label="Urgence"
                                    value={edit.urgency || ""}
                                    onChange={(e) => setEdit({ ...edit, urgency: e.target.value })}
                                    options={URGENCY_LIST}
                                    testid="dossier-urgency"
                                />
                                <Select
                                    label="Besoin réseau XV"
                                    value={edit.network_need || ""}
                                    onChange={(e) => setEdit({ ...edit, network_need: e.target.value })}
                                    options={NETWORK_NEED_LIST}
                                    testid="dossier-network-need"
                                />
                                <Select
                                    label="Potentiel success fee"
                                    value={edit.success_fee_potential || ""}
                                    onChange={(e) => setEdit({ ...edit, success_fee_potential: e.target.value })}
                                    options={SUCCESS_POT_LIST}
                                    testid="dossier-success-pot"
                                />
                                <div className="flex flex-col">
                                    <label className="text-[10px] tracking-[0.28em] uppercase text-[#d4af37]/80 mb-3">
                                        Dimension internationale
                                    </label>
                                    <label className="inline-flex items-center gap-3 cursor-pointer mt-2">
                                        <input
                                            type="checkbox"
                                            data-testid="dossier-international"
                                            checked={!!edit.international_dim}
                                            onChange={(e) => setEdit({ ...edit, international_dim: e.target.checked })}
                                            className="accent-[#d4af37]"
                                        />
                                        <span className="text-[13px] font-serif text-[#f4ecd8]/85">
                                            Mission internationale
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                                Synthèse stratégique
                            </label>
                            <textarea
                                rows={4}
                                value={edit.summary}
                                onChange={(e) => setEdit({ ...edit, summary: e.target.value })}
                                className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                            />
                        </div>
                    </div>
                    <div className="mt-8 flex justify-end">
                        <button
                            type="button"
                            data-testid="dossier-save"
                            onClick={saveMeta}
                            className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                        >
                            Enregistrer
                        </button>
                    </div>
                </section>
            )}

            {dossier.summary && (
                <section className="mb-14 max-w-3xl">
                    <p className="text-[15px] text-[#f4ecd8]/75 leading-[1.85] font-light whitespace-pre-line">
                        {dossier.summary}
                    </p>
                </section>
            )}

            {/* TIMELINE */}
            <section className="mb-16">
                <div className="overline text-[#d4af37]/70 mb-3">Avancement</div>
                <h2 className="font-serif text-[24px] text-[#f4ecd8] mb-4" style={{ fontWeight: 400 }}>
                    Timeline de l'opération
                </h2>
                <p className="mb-10 text-[12px] text-[#f4ecd8]/45 font-light italic max-w-2xl leading-relaxed">
                    Cliquez sur le statut d'une étape pour la faire avancer.
                    Chaque étape peut héberger un compte rendu visible côté
                    client une fois validé.
                </p>
                <ul className="space-y-1">
                    {dossier.timeline.map((s) => {
                        const Icon = s.state === "done" ? Check : s.state === "in_progress" ? Circle : CircleDashed;
                        const hasReport =
                            s.report && (s.report.body || "").trim().length > 0;
                        const isOpen = openStepId === s.id;
                        return (
                            <li
                                key={s.id}
                                data-testid={`timeline-step-row-${s.order}`}
                                className="border-b border-white/[0.06]"
                            >
                                <div className="w-full flex items-center gap-4 sm:gap-6 py-4 hover:bg-white/[0.02] transition-colors duration-500 px-2">
                                    <button
                                        type="button"
                                        data-testid={`timeline-step-${s.order}`}
                                        onClick={() => toggleStep(s.id)}
                                        title="Faire avancer l'étape"
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-500 ${
                                            s.state === "done"
                                                ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]"
                                                : s.state === "in_progress"
                                                  ? "border-[#d4af37]/70 text-[#d4af37]/90"
                                                  : "border-white/15 text-[#f4ecd8]/30"
                                        }`}
                                    >
                                        <Icon strokeWidth={1.4} size={16} />
                                    </button>
                                    <span className="text-[10px] tracking-[0.32em] text-[#d4af37]/60 w-8 shrink-0 hidden sm:inline">
                                        {String(s.order).padStart(2, "0")}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenStepId(isOpen ? null : s.id)
                                        }
                                        data-testid={`timeline-step-toggle-${s.order}`}
                                        className="flex-1 text-left min-w-0"
                                    >
                                        <div
                                            className={`font-serif text-[17px] sm:text-[19px] md:text-[22px] leading-tight ${
                                                s.state === "done"
                                                    ? "text-[#f4ecd8]"
                                                    : "text-[#f4ecd8]/80"
                                            }`}
                                            style={{ fontWeight: 400 }}
                                        >
                                            {s.label}
                                        </div>
                                        <div className="mt-1 flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase">
                                            <span className="text-[#f4ecd8]/40">
                                                {s.state === "done"
                                                    ? "Terminé"
                                                    : s.state === "in_progress"
                                                      ? "En cours"
                                                      : "À venir"}
                                            </span>
                                            {hasReport && (
                                                <span
                                                    className={`inline-flex items-center gap-1 ${
                                                        s.report.validated
                                                            ? "text-[#d4af37]/85"
                                                            : "text-[#f4ecd8]/55"
                                                    }`}
                                                >
                                                    <FileText
                                                        strokeWidth={1.3}
                                                        size={10}
                                                    />
                                                    {s.report.validated
                                                        ? "CR validé"
                                                        : "Brouillon"}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleStep(s.id)}
                                        data-testid={`timeline-step-advance-${s.order}`}
                                        title="Faire avancer l'étape (À venir → En cours → Terminé)"
                                        className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/55 hover:text-[#d4af37] px-3 py-1.5 border border-white/10 hover:border-[#d4af37]/40 transition-all duration-500 hidden sm:inline-flex"
                                    >
                                        {s.state === "done"
                                            ? "Réouvrir"
                                            : s.state === "in_progress"
                                              ? "Terminer"
                                              : "Démarrer"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenStepId(isOpen ? null : s.id)
                                        }
                                        data-testid={`timeline-step-cr-${s.order}`}
                                        className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80 hover:text-[#d4af37] flex items-center gap-2 px-3 py-1.5 border border-[#d4af37]/30 hover:border-[#d4af37]/60 transition-all duration-500"
                                    >
                                        {hasReport ? "Compte rendu" : "+ CR"}
                                        {isOpen ? (
                                            <ChevronUp
                                                strokeWidth={1.3}
                                                size={12}
                                            />
                                        ) : (
                                            <ChevronDown
                                                strokeWidth={1.3}
                                                size={12}
                                            />
                                        )}
                                    </button>
                                </div>
                                {isOpen && (
                                    <StepReportsManager
                                        key={`mgr-${s.id}`}
                                        step={s}
                                        onAdd={(payload) =>
                                            addStepReport(s.id, payload)
                                        }
                                        onUpdate={(reportId, payload) =>
                                            updateStepReportById(s.id, reportId, payload)
                                        }
                                        onDelete={(reportId) =>
                                            deleteStepReportById(s.id, reportId)
                                        }
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            </section>

            {/* DOCUMENTS + NOTES */}
            <section className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-6">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <div className="overline text-[#d4af37]/70 mb-3">
                                Coffre-fort documentaire
                            </div>
                            <h2 className="font-serif text-[22px] text-[#f4ecd8]" style={{ fontWeight: 400 }}>
                                Documents
                            </h2>
                        </div>
                        <label
                            data-testid="doc-upload"
                            className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                        >
                            <Upload strokeWidth={1.2} size={12} />
                            Déposer
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={uploadFile}
                                disabled={busy}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <div className="border-t border-white/[0.06]">
                        {docs.length === 0 ? (
                            <p className="text-[#f4ecd8]/40 text-sm py-6 italic">
                                Aucun document pour ce dossier.
                            </p>
                        ) : (
                            docs.map((d) => (
                                <div
                                    key={d.id}
                                    className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 py-4 border-b border-white/[0.06] px-2"
                                >
                                    <File strokeWidth={1} size={18} className="text-[#d4af37]/70" />
                                    <button
                                        type="button"
                                        onClick={() => downloadDoc(d)}
                                        className="text-left text-[14px] font-serif text-[#f4ecd8] hover:text-[#d4af37] transition-colors duration-500 truncate"
                                    >
                                        {d.filename}
                                    </button>
                                    <span className="text-[11px] text-[#f4ecd8]/40 tracking-wide">
                                        {formatBytes(d.size)}
                                    </span>
                                    <button
                                        type="button"
                                        data-testid={`doc-share-${d.id}`}
                                        onClick={() => toggleShare(d)}
                                        title={
                                            d.shared_with_client
                                                ? "Visible côté client — cliquer pour masquer"
                                                : "Privé interne — cliquer pour partager"
                                        }
                                        className={`text-[10px] tracking-[0.24em] uppercase px-2.5 py-1 border transition-all duration-500 ${
                                            d.shared_with_client
                                                ? "border-[#d4af37]/60 text-[#d4af37]"
                                                : "border-white/15 text-[#f4ecd8]/40 hover:border-[#d4af37]/40 hover:text-[#d4af37]/80"
                                        }`}
                                    >
                                        {d.shared_with_client ? "Client" : "Interne"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteDoc(d.id)}
                                        className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors duration-500"
                                    >
                                        <Trash2 strokeWidth={1.2} size={14} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="lg:col-span-6">
                    <div className="overline text-[#d4af37]/70 mb-3">Notes internes</div>
                    <h2 className="font-serif text-[22px] text-[#f4ecd8] mb-8" style={{ fontWeight: 400 }}>
                        Mémo confidentiel
                    </h2>
                    <form onSubmit={addNote} className="mb-6">
                        <textarea
                            rows={3}
                            value={newNote}
                            data-testid="new-note-input"
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Ajouter une note interne…"
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none placeholder-[#f4ecd8]/30"
                        />
                        <div className="mt-3 flex justify-end">
                            <button
                                type="submit"
                                data-testid="new-note-submit"
                                disabled={busy || !newNote.trim()}
                                className="px-5 py-2.5 text-[10px] tracking-[0.3em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Ajouter
                            </button>
                        </div>
                    </form>
                    <div className="border-t border-white/[0.06]">
                        {notes.length === 0 ? (
                            <p className="text-[#f4ecd8]/40 text-sm py-6 italic">
                                Aucune note interne.
                            </p>
                        ) : (
                            notes.map((n) => (
                                <div key={n.id} className="py-5 border-b border-white/[0.06]">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-[11px] text-[#d4af37]/70 tracking-wide">
                                            {n.author_name || n.author_email} · {new Date(n.created_at).toLocaleString("fr-FR")}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => deleteNote(n.id)}
                                            className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors duration-500"
                                        >
                                            <Trash2 strokeWidth={1.2} size={13} />
                                        </button>
                                    </div>
                                    <p className="text-[14px] text-[#f4ecd8]/85 leading-[1.85] font-light whitespace-pre-line">
                                        {n.body}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            {/* CLIENT INVITES */}
            <InvitesPanel dossierId={id} />

            {/* CLIENT ACCESS — accounts + login history + resend */}
            <ClientAccessPanel dossierId={id} />

            {/* DOSSIER CONTACTS — interlocutors directory */}
            <DossierContactsPanel dossierId={id} />

            {/* FINANCIAL PANEL */}
            <FinancialPanel
                dossier={dossier}
                onDossierUpdate={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* CLIENT CONTRIBUTIONS */}
            <ClientContributionsPanel dossierId={id} />

            {/* CLIENT REPORTS */}
            <ReportsPanel dossierId={id} />

            {/* OPPORTUNITIES — internal vision */}
            <OpportunitiesPanel
                dossier={dossier}
                onDossierUpdate={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* AI COACHING — private admin-only suggestion */}
            <AdminCoachingBlock
                dossier={dossier}
                onUpdated={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* VALUATION — Évaluation économique de la mission XV (interne) */}
            <ValuationPanel
                dossier={dossier}
                onUpdated={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* ADMIN IMPACT — Contribution fiscale du dossier (interne) */}
            <DossierAdminImpact dossierId={id} />

            {/* STRATEGIC READING — cockpit de décision */}
            <StrategicReadingPanel dossier={dossier} />

            {/* CLIENT SHAREABLES — expertises / questions / réflexions */}
            <ClientShareablesPanel
                dossier={dossier}
                onDossierUpdate={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* NETWORK LINKS — partners + prescriber + mobilized interlocutors */}
            <NetworkLinksPanel
                dossier={dossier}
                partners={partners}
                onDossierUpdate={(d) => {
                    setDossier(d);
                    setEdit(d);
                }}
            />

            {/* AI INTELLIGENCE — internal */}
            <AIIntelligence dossierId={id} partners={partners} />

            {/* INVESTMENT OFFICE MODULES — optional premium modules */}
            <CockpitDossierModulesPanel dossierId={id} />

            {/* COMMITTEE SIMULATOR — admin view */}
            <CockpitCommitteePanel dossierId={id} />
        </div>
    );
}

function Tag({ label, value, accent }) {
    return (
        <span
            className={`inline-flex items-center gap-2 ${
                accent ? "text-[#d4af37]" : "text-[#f4ecd8]/70"
            }`}
        >
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#f4ecd8]/35">
                {label}
            </span>
            <span className="font-serif text-[14px]">{value}</span>
        </span>
    );
}


function StepReportsManager({ step, onAdd, onUpdate, onDelete }) {
    // Existing reports (canonical: step.reports[]; fallback: legacy single)
    const list = (() => {
        if (Array.isArray(step.reports) && step.reports.length > 0) {
            return step.reports;
        }
        if (step.report && ((step.report.title || "").trim() || (step.report.body || "").trim())) {
            return [step.report];
        }
        return [];
    })();
    const [adding, setAdding] = useState(list.length === 0);
    const [editingId, setEditingId] = useState(null);

    return (
        <div
            data-testid={`step-reports-${step.order}`}
            className="bg-[#070d18] border border-[#d4af37]/15 px-5 sm:px-8 py-7 mb-2 mx-2"
        >
            <div className="overline text-[#d4af37]/80 mb-5 flex items-center gap-2">
                <FileText strokeWidth={1.3} size={14} />
                Sous-conversations · {step.label}
                {list.length > 0 && (
                    <span className="ml-auto text-[10px] tracking-[0.22em] text-[#d4af37]/65">
                        {list.length} synthèse{list.length > 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {/* List of existing reports */}
            {list.length === 0 ? (
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light mb-5">
                    Aucune synthèse. Ajoutez la première sous-conversation
                    pour cette étape.
                </p>
            ) : (
                <ul
                    data-testid={`step-reports-list-${step.order}`}
                    className="space-y-3 mb-6"
                >
                    {list.map((r) => (
                        <ReportRow
                            key={r.id || r.updated_at}
                            step={step}
                            report={r}
                            isEditing={editingId === (r.id || "legacy")}
                            onEdit={() => setEditingId(r.id || "legacy")}
                            onCancel={() => setEditingId(null)}
                            onSave={async (payload) => {
                                await onUpdate(r.id, payload);
                                setEditingId(null);
                            }}
                            onDelete={() => onDelete(r.id)}
                        />
                    ))}
                </ul>
            )}

            {/* Add new */}
            {adding ? (
                <ReportEditor
                    step={step}
                    report={null}
                    onCancel={list.length > 0 ? () => setAdding(false) : null}
                    onSave={async (payload) => {
                        await onAdd(payload);
                        setAdding(false);
                    }}
                />
            ) : (
                <div className="flex justify-end">
                    <button
                        type="button"
                        data-testid={`step-add-report-${step.order}`}
                        onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-3 px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                    >
                        + Ajouter une sous-conversation
                    </button>
                </div>
            )}
        </div>
    );
}

function ReportRow({
    step,
    report: r,
    isEditing,
    onEdit,
    onCancel,
    onSave,
    onDelete,
}) {
    if (isEditing) {
        return (
            <li>
                <ReportEditor
                    step={step}
                    report={r}
                    onCancel={onCancel}
                    onSave={onSave}
                />
            </li>
        );
    }
    const validated = !!r.validated;
    const updated = r.updated_at
        ? new Date(r.updated_at).toLocaleString("fr-FR")
        : "";
    return (
        <li
            data-testid={`step-report-${r.id || "legacy"}`}
            className="border border-white/[0.08] bg-white/[0.02] px-4 py-3.5"
        >
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                        <span
                            className={`inline-flex items-center gap-1.5 text-[9.5px] tracking-[0.24em] uppercase ${
                                validated
                                    ? "text-[#d4af37]"
                                    : "text-[#f4ecd8]/45"
                            }`}
                        >
                            {validated ? (
                                <Eye strokeWidth={1.3} size={11} />
                            ) : (
                                <EyeOff strokeWidth={1.3} size={11} />
                            )}
                            {validated ? "Publié" : "Brouillon"}
                        </span>
                        {updated && (
                            <span className="text-[10px] tracking-[0.18em] uppercase text-[#f4ecd8]/35">
                                · {updated}
                            </span>
                        )}
                    </div>
                    <div
                        className="font-serif text-[15px] text-[#f4ecd8]/90 leading-tight truncate"
                        style={{ fontWeight: 400 }}
                    >
                        {r.title || "Sans titre"}
                    </div>
                    {r.body && (
                        <p className="mt-1 text-[12.5px] text-[#f4ecd8]/55 font-light leading-relaxed line-clamp-2">
                            {r.body}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onEdit}
                        data-testid={`step-report-edit-${r.id || "legacy"}`}
                        className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/55 hover:text-[#d4af37] transition-colors duration-500"
                    >
                        Modifier
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid={`step-report-delete-${r.id || "legacy"}`}
                        className="text-[#f4ecd8]/35 hover:text-[#e89090] transition-colors duration-500"
                        title="Supprimer"
                    >
                        <Trash2 strokeWidth={1.2} size={13} />
                    </button>
                </div>
            </div>
        </li>
    );
}

function ReportEditor({ step, report, onSave, onCancel }) {
    const r = report || {};
    const [title, setTitle] = useState(r.title || "");
    const [body, setBody] = useState(r.body || "");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const submit = async (publish) => {
        if (!body.trim() && !title.trim()) {
            setMsg("Titre ou contenu requis.");
            return;
        }
        setBusy(true);
        setMsg("");
        try {
            await onSave({ title, body, validated: publish });
            setMsg(publish ? "Publié auprès du client." : "Brouillon enregistré.");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid={`step-report-editor-${step.order}`}
            className="bg-[#0b1220] border border-[#d4af37]/30 p-5 sm:p-6"
        >
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                Titre de la sous-conversation
            </label>
            <input
                data-testid={`step-report-title-${step.order}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`Synthèse · ${step.label}`}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[15px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 transition-colors duration-500 mb-5"
            />
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-2">
                Contenu (visible par le client lorsque publié)
            </label>
            <textarea
                data-testid={`step-report-body-${step.order}`}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder="Rédigez ici la synthèse confidentielle pour cette étape…"
                className="w-full bg-transparent border border-white/10 focus:border-[#d4af37]/40 outline-none px-4 py-3 text-[14px] leading-[1.85] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25 transition-colors duration-500 resize-y"
            />

            {msg && (
                <div
                    data-testid={`step-report-msg-${step.order}`}
                    className="mt-4 text-[12px] text-[#d4af37]/85 border-l-2 border-[#d4af37] pl-3 py-1 italic"
                >
                    {msg}
                </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    data-testid={`step-report-save-draft-${step.order}`}
                    disabled={busy || (!body.trim() && !title.trim())}
                    onClick={() => submit(false)}
                    className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-white/20 text-[#f4ecd8]/80 hover:text-[#f4ecd8] hover:border-white/40 transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    Enregistrer brouillon
                </button>
                <button
                    type="button"
                    data-testid={`step-report-publish-${step.order}`}
                    disabled={busy || !body.trim()}
                    onClick={() => submit(true)}
                    className="px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    {r.validated ? "Mettre à jour" : "Publier au client"}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="ml-auto text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Annuler
                    </button>
                )}
            </div>
        </div>
    );
}


function ClientContributionsPanel({ dossierId }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(true);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        cockpit
            .get(`/dossiers/${dossierId}/contributions`)
            .then(({ data }) => {
                setItems(data);
                setLoaded(true);
            })
            .catch(() => setLoaded(true));
    }, [dossierId]);

    if (loaded && items.length === 0) {
        return (
            <section
                data-testid="cockpit-contributions-empty"
                className="px-5 sm:px-8 lg:px-16 py-10 border-t border-white/[0.06]"
            >
                <div className="overline text-[#d4af37]/70 mb-2">
                    Contributions client
                </div>
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light">
                    Aucune contribution transmise par le client pour ce dossier.
                </p>
            </section>
        );
    }

    return (
        <section
            data-testid="cockpit-contributions"
            className="px-5 sm:px-8 lg:px-16 py-10 border-t border-white/[0.06]"
        >
            <div className="flex items-baseline justify-between mb-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-2">
                        Contributions client
                    </div>
                    <h2
                        className="font-serif text-[22px] text-[#f4ecd8]"
                        style={{ fontWeight: 400 }}
                    >
                        Informations transmises par le client ({items.length})
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="text-[#d4af37]/70 hover:text-[#d4af37] transition-colors"
                    aria-label={open ? "Replier" : "Déplier"}
                >
                    {open ? (
                        <ChevronUp strokeWidth={1.3} size={18} />
                    ) : (
                        <ChevronDown strokeWidth={1.3} size={18} />
                    )}
                </button>
            </div>
            {open && (
                <ul className="space-y-4">
                    {items.map((c) => (
                        <li
                            key={c.id}
                            data-testid={`cockpit-contribution-${c.id}`}
                            className={`bg-[#070d18] border px-6 py-5 ${
                                c.confidential
                                    ? "border-[#d4af37]/35"
                                    : "border-white/[0.06]"
                            }`}
                        >
                            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
                                <div className="font-serif text-[17px] text-[#f4ecd8]" style={{ fontWeight: 400 }}>
                                    {c.title}
                                </div>
                                {c.confidential && (
                                    <span className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.28em] uppercase text-[#d4af37]">
                                        <Lock strokeWidth={1.3} size={10} />
                                        XV uniquement
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/40 mb-3">
                                {c.date
                                    ? new Date(c.date).toLocaleDateString("fr-FR", {
                                          day: "2-digit",
                                          month: "long",
                                          year: "numeric",
                                      })
                                    : "—"}
                                {c.author_name ? ` · ${c.author_name}` : ""}
                            </div>
                            {c.note && (
                                <p className="font-serif text-[13.5px] text-[#f4ecd8]/80 leading-[1.85] whitespace-pre-wrap">
                                    {c.note}
                                </p>
                            )}
                            {c.attachment?.filename && (
                                <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-[#d4af37]/85">
                                    <Paperclip strokeWidth={1.3} size={12} />
                                    {c.attachment.filename}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// AdminCoachingBlock — private AI-generated coaching note,
// visible only inside the cockpit (never exposed to the client).
// ─────────────────────────────────────────────────────────────
function AdminCoachingBlock({ dossier, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const coaching = dossier.admin_coaching || null;

    const generate = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossier.id}/ai-coaching`,
            );
            onUpdated?.({ ...dossier, admin_coaching: data.coaching });
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Suggestion IA indisponible — réessayez.",
            );
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        if (!window.confirm("Supprimer la suggestion IA ?")) return;
        try {
            await cockpit.delete(`/dossiers/${dossier.id}/ai-coaching`);
            const next = { ...dossier };
            delete next.admin_coaching;
            onUpdated?.(next);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            return d.toLocaleString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    return (
        <section
            data-testid="admin-coaching"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-5">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3">
                        Suggestion d'accompagnement · interne
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Note privée IA
                    </h2>
                    <p className="mt-2 text-[12px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Lecture stratégique générée par Claude à partir des
                        données du dossier. Visible <strong>uniquement</strong>{" "}
                        depuis le cockpit, jamais exposée au client.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {coaching && (
                        <button
                            type="button"
                            onClick={clear}
                            data-testid="coaching-clear"
                            className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/50 hover:text-[#e89090] transition-colors duration-500"
                        >
                            Effacer
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={generate}
                        disabled={busy}
                        data-testid="coaching-generate"
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.28em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {busy ? "Génération…" : coaching ? "Régénérer" : "Générer suggestion IA"}
                    </button>
                </div>
            </div>
            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1 mb-4">
                    {err}
                </div>
            )}
            {coaching ? (
                <div
                    data-testid="coaching-body"
                    className="bg-[#070d18] border border-[#d4af37]/20 p-6 lg:p-8"
                >
                    <p className="font-serif text-[14.5px] text-[#f4ecd8]/90 leading-[1.85] whitespace-pre-wrap">
                        {coaching.body}
                    </p>
                    <div className="mt-5 pt-4 border-t border-white/[0.06] text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65">
                        Générée le {formatDate(coaching.generated_at)}
                    </div>
                </div>
            ) : (
                <p className="text-[12.5px] text-[#f4ecd8]/35 italic font-light">
                    Aucune suggestion. Lancez la génération pour obtenir une
                    lecture stratégique privée.
                </p>
            )}
        </section>
    );
}

